import { Component, OnDestroy, OnInit } from "@angular/core";
import moment from "moment";
import { NgxSpinnerService } from "ngx-spinner";
import { ConfirmationService } from "primeng/api";
import { Subscription } from "rxjs";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { LocalDataStoreService } from "src/app/core/services/local-data-store.service";
import { UserService } from "src/app/core/services/user.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { Dealer } from "src/app/shared/models/Dealer.model";
import { UserRoleEnum } from "src/app/shared/models/enums/user-role.enum";
import { Store } from "src/app/shared/models/store";
import { VenueGroup } from "src/app/shared/models/VenueGroup.entity";
import { VenueGroupVenue } from "src/app/shared/models/VenueGroupVenue.entity";

@Component({
	selector: "app-venue-group-setup",
	templateUrl: "./venue-group-setup.component.html",
	styleUrls: ["../../commandcenter.scss"],
})
export class VenueGroupSetupComponent implements OnInit, OnDestroy {
	changesToSave: boolean = false;
	dealers: Dealer[];

	loaded: boolean = false;

	moment: any = moment;

	venueList: Store[];
	venueKey: number;
	venueChangeSubscription: Subscription;
	venueGroups: VenueGroup[];
	venueGroupSelected: VenueGroup;
	venueGroupSelectedVenueKeys: number[];

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private localStorage: LocalDataStoreService,
		private spinner: NgxSpinnerService,
		public userService: UserService,
		public venueSvc: OOVenueService,
		private confirmationService: ConfirmationService
	) {}

	ngOnInit(): void {
		this.spinner.show();
		this.init();
	}

	ngOnDestroy() {
		this.venueChangeSubscription.unsubscribe();
	}

	async init() {
		const venue = await this.localStorage.get("venue");
		this.venueKey = venue.key;
		await this.load();
		this.venueChangeSubscription = this.localStorage.getVenue().subscribe(async (data) => {
			if (this.venueKey !== data.key) {
				this.spinner.show();

				this.venueKey = data.key;
				await this.load();
			}
		});

		if (!this.venueSvc.ooVenue) {
			console.log("No OOV");
		}
		this.loaded = true;
		this.spinner.hide();
	}

	async load() {
		if (!this.userService.user) {
			this.userService.user = await this.localStorage.get("user");
		}
		switch (this.userService.user.role) {
			case UserRoleEnum.GOD:
				await this.venueGroupGetAll();
				this.dealersGet();
				break;
			case UserRoleEnum.SUPERADMIN:
				await this.venueGroupGetAll();
				this.dealersGet();
				break;
			case UserRoleEnum.DEALERADMIN:
				await this.venueGroupGetByDealerKey(this.userService.user.dealerKey);
				this.dealerVenuesGet(this.userService.user.dealerKey);
				break;
			case UserRoleEnum.DEALERUSER:
				await this.venueGroupGetByDealerKey(this.userService.user.dealerKey);
				this.dealerVenuesGet(this.userService.user.dealerKey);
				break;
			case UserRoleEnum.VENUEUSER:
				await this.venueGroupGetByVenueKey(this.venueKey);
				this.userVenuesList();
				break;
			case UserRoleEnum.VENUEADMIN:
				await this.venueGroupGetByVenueKey(this.venueKey);
				this.userVenuesList();
				break;
			default:
				await this.venueGroupGetByVenueKey(this.venueKey);
				this.userVenuesList();
				break;
		}
	}

	async selectVenueGroup(rec: VenueGroup) {
		if (this.venueGroupSelected.dealerKey) {
			await this.dealerVenuesGet(this.venueGroupSelected.dealerKey);
		}
		this.venueGroupSelectedVenueKeys = this.venueGroupSelected.VenueGroupVenue.map((i) => i.venueKey);
	}

	async userVenuesList() {
		const venues = await this.localStorage.get("venues");
		this.venueList = venues.map((i) => {
			return new Store(i);
		});
		this.venueList = this.sortVenues(this.venueList);
	}

	async dealerVenuesGet(dealerKey) {
		const resp = await this.focuslink.venueGetAll(dealerKey, true);
		this.venueList = resp.map((i) => {
			return new Store(i);
		});
		this.venueList = this.sortVenues(this.venueList);
	}

	sortVenues(venues: Store[]): Store[] {
		return venues.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
	}

	async venueGroupAdd() {
		this.venueGroups.push(new VenueGroup());
		this.venueGroupSelected = this.venueGroups[this.venueGroups.length - 1];
	}

	async venueGroupGetByVenueKey(venueKey: number) {
		const resp = await this.focuslink.venueGroupGetByVenue(venueKey);
		this.venueGroups = resp.map((i) => {
			return new VenueGroup(i);
		});
	}

	async venueGroupGetAll() {
		const resp = await this.focuslink.venueGroupGetAll();
		this.venueGroups = resp.map((i) => {
			return new VenueGroup(i);
		});
	}

	async venueGroupGetByDealerKey(dealerKey: string) {
		const resp = await this.focuslink.venueGroupGetByDealerKey(dealerKey);
		this.venueGroups = resp.map((i) => {
			return new VenueGroup(i);
		});
	}

	async venueGroupDelete(rec: VenueGroup) {
		await this.confirmationService.confirm({
			message: `Are you sure you want to delete Venue Group ${rec.name}? This cannot be undone.`,
			header: "Confirmation",
			icon: "pi pi-exclamation-triangle",
			accept: async () => {
				await this.focuslink.venueGroupDelete(rec.key);
				this.venueGroups.splice(
					this.venueGroups.findIndex((i) => i.key === rec.key),
					1
				);
			},
			reject: (type) => {
				this.confirmationService.close();
			},
		});
	}

	async venueGroupPost() {
		delete this.venueGroupSelected.VenueGroupVenue;
		this.venueGroupSelected.VenueGroupVenue = this.venueGroupSelectedVenueKeys.map((i) => {
			return new VenueGroupVenue({
				venueGroupKey: this.venueGroupSelected.key,
				venueKey: i,
			});
		});
		await this.focuslink.venueGroupPost(this.venueGroupSelected);
	}

	async dealersGet() {
		const resp = await this.focuslink.dealersGet();
		this.dealers = resp.map((i) => {
			return new Dealer(i);
		});
	}
}
